import { useState, useEffect } from "react";
import axios from "axios";
import moment from "moment";
import { useSelector } from "react-redux";
import Sidebar from "../../GlobalFiles/Sidebar";
import PatientEditModal from "../../GlobalFiles/PatientEditModal";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../../../../../api";
import { getImageUrl, handleImageError } from "../../../../../helper/imageHelper";

const PAGE_SIZE = 10;

const AdminDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [agents, setAgents] = useState([]);
  const [managers, setmanagers] = useState([]);
  const [patients, setPatientsList] = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [admins, setAdmins] = useState([]);
  // const [appointments, setAppointments] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState("view"); // view, edit
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedUserType, setSelectedUserType] = useState(null);
  const [formData, setFormData] = useState({});
  const [doctorPage, setDoctorPage] = useState(0);
  const [managerPage, setManagerPage] = useState(0);
  const [agentPage, setAgentPage] = useState(0);
  const [adminPage, setAdminPage] = useState(0);
  const [patientPage, setPatientPage] = useState(0);
  const [summaryCounts, setSummaryCounts] = useState({});
  const [deleteLoading, setDeleteLoading] = useState({});
  const [statusLoading, setStatusLoading] = useState({});
  const [editPatientModalVisible, setEditPatientModalVisible] = useState(false);
  const [editPatientData, setEditPatientData] = useState(null);
  const [editPatientLoading, setEditPatientLoading] = useState(false);
  const [addPatientModalVisible, setAddPatientModalVisible] = useState(false);
  const [addPatientData, setAddPatientData] = useState({ patientName: "", mobile: "", age: "", gender: "", address: "" });
  const [addPatientLoading, setAddPatientLoading] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteRelations, setDeleteRelations] = useState(null);
  const [isFetchingRelations, setIsFetchingRelations] = useState(false);

  const {
    data: { user },
  } = useSelector((state) => state.auth);

  const isAdmin = user?.userType === "admin";

  useEffect(() => {
    if (isAdmin) {
      fetchByTab(activeTab);
    }
  }, [isAdmin, activeTab]);

  useEffect(() => {
    if (isAdmin) {
      fetchSummaryCounts();
    }
  }, [isAdmin]);

  const fetchSummaryCounts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/summary`);
      setSummaryCounts(response.data || {});
    } catch (error) {
      console.error("Error fetching admin summary:", error);
    }
  };

  useEffect(() => {
    const maxPage = Math.max(Math.ceil(doctors.length / PAGE_SIZE) - 1, 0);
    setDoctorPage((prev) => Math.min(prev, maxPage));
  }, [doctors.length]);

  useEffect(() => {
    const maxPage = Math.max(Math.ceil(managers.length / PAGE_SIZE) - 1, 0);
    setManagerPage((prev) => Math.min(prev, maxPage));
  }, [managers.length]);

  useEffect(() => {
    const maxPage = Math.max(Math.ceil(agents.length / PAGE_SIZE) - 1, 0);
    setAgentPage((prev) => Math.min(prev, maxPage));
  }, [agents.length]);

  useEffect(() => {
    const maxPage = Math.max(Math.ceil(admins.length / PAGE_SIZE) - 1, 0);
    setAdminPage((prev) => Math.min(prev, maxPage));
  }, [admins.length]);

  useEffect(() => {
    const maxPage = Math.max(Math.ceil(patients.length / PAGE_SIZE) - 1, 0);
    setPatientPage((prev) => Math.min(prev, maxPage));
  }, [patients.length]);

  const fetchByTab = async (tab) => {
    setLoading(true);
    try {
      switch (tab) {
        case "doctors":
          await fetchDoctors();
          break;
        case "managers":
          await fetchmanagers();
          break;
        case "agents":
          await fetchAgents();
          break;
        case "admins":
          await fetchAdmins();
          break;
        case "patients":
          await fetchPatients();
          break;
        default:
          break;
      }
    } catch (error) {
      toast.error("Error fetching data");
    }
    setLoading(false);
  };

  const fetchDoctors = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/doctors`);
      setDoctors(response.data);
    } catch (error) {
      console.error("Error fetching doctors:", error);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/agents`);
      setAgents(response.data);
    } catch (error) {
      console.error("Error fetching agents:", error);
    }
  };

  const fetchmanagers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/managers`);
      setmanagers(response.data);
    } catch (error) {
      console.error("Error fetching managers:", error);
    }
  };

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin`);
      console.log(`[Frontend] fetchAdmins received:`, response.data.map(a => ({ id: a._id || a.id, status: a.status })));
      setAdmins(response.data);
    } catch (error) {
      console.error("Error fetching admins:", error);
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/patients`, {
        headers: { "x-actor-type": "admin" },
      });
      const patientList = response.data?.patients || response.data || [];
      setPatientsList(patientList);
    } catch (error) {
      toast.error("Failed to fetch patients");
      console.error("Error fetching patients:", error);
    }
  };

  const handleEditPatientChange = (field, value) => {
    setEditPatientData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditPatientSubmit = async () => {
    if (!editPatientData?._id) return;

    setEditPatientLoading(true);
    try {
      await axios.patch(`${API_BASE_URL}/patients/${editPatientData._id}`, editPatientData);
      toast.success("Patient updated successfully");
      setPatientsList((prev) => prev.map((p) => (p._id === editPatientData._id ? editPatientData : p)));
      setEditPatientModalVisible(false);
      setEditPatientData(null);
      await fetchSummaryCounts();
    } catch (error) {
      toast.error("Failed to update patient");
      console.error("Patient update error:", error);
    } finally {
      setEditPatientLoading(false);
    }
  };

  const handleAddPatientChange = (field, value) => {
    setAddPatientData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddPatientSubmit = async () => {
    if (!addPatientData.patientName || !addPatientData.mobile) {
      toast.error("Name and Mobile are required");
      return;
    }

    setAddPatientLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/patients/register`, addPatientData);
      if (response.data.message === "Patient already exists") {
        toast.error("A patient with this mobile number already exists");
      } else {
        toast.success("Patient added successfully");
        setAddPatientModalVisible(false);
        setAddPatientData({ patientName: "", mobile: "", age: "", gender: "", address: "" });
        await fetchPatients();
        await fetchSummaryCounts();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to add patient");
      console.error("Patient add error:", error);
    } finally {
      setAddPatientLoading(false);
    }
  };

  const getDeleteKey = (record, userType) => {
    const recordId = record?._id || record?.id || "";
    return `${userType}:${recordId}`;
  };

  const confirmDeletion = async (record, userType) => {
    const recordId = record?._id || record?.id;
    if (!recordId) return;

    setUserToDelete({ record, userType });
    setDeleteRelations(null);
    setDeleteModalVisible(true);

    if (userType === "patient") {
      setIsFetchingRelations(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/admin/relations/${userType}/${recordId}`);
        setDeleteRelations(response.data);
      } catch (error) {
        console.error("Error fetching relations:", error);
      } finally {
        setIsFetchingRelations(false);
      }
    }
  };

  const proceedWithDeletion = async () => {
    if (userToDelete) {
      await handleDelete(userToDelete.record, userToDelete.userType);
      setDeleteModalVisible(false);
      setUserToDelete(null);
      setDeleteRelations(null);
    }
  };

  const handleDelete = async (record, userType) => {
    const deleteKey = getDeleteKey(record, userType);
    try {
      setDeleteLoading((prev) => ({ ...prev, [deleteKey]: true }));
      let endpoint = "";
      let idField = "";

      switch (userType) {
        case "doctor":
          endpoint = "doctors";
          idField = record._id;
          break;
        case "manager":
          endpoint = "managers";
          idField = record._id;
          break;
        case "agent":
          endpoint = "agents";
          idField = record._id;
          break;
        case "admin":
          endpoint = "admin";
          idField = record._id;
          break;
        case "patient":
          endpoint = "patients";
          idField = record._id;
          break;
        case "appointment":
          endpoint = "appointments";
          idField = record._id;
          break;
        default:
          return;
      }

      await axios.delete(`${API_BASE_URL}/${endpoint}/${idField}`);
      toast.success(`${userType} deleted successfully`);
      if (userType === "doctor") await fetchDoctors();
      if (userType === "manager") await fetchmanagers();
      if (userType === "agent") await fetchAgents();
      if (userType === "admin") await fetchAdmins();
      if (userType === "patient") await fetchPatients();
      await fetchSummaryCounts();
    } catch (error) {
      toast.error(`Error deleting ${userType}`);
      console.error("Delete error:", error);
    } finally {
      setDeleteLoading((prev) => ({ ...prev, [deleteKey]: false }));
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      let endpoint = "";
      let idField = "";

      switch (selectedUserType) {
        case "doctor":
          endpoint = "doctors";
          idField = selectedRecord._id;
          break;
        case "manager":
          endpoint = "managers";
          idField = selectedRecord._id;
          break;
        case "agent":
          endpoint = "agents";
          idField = selectedRecord._id;
          break;
        case "admin":
          endpoint = "admin";
          idField = selectedRecord._id;
          break;
        default:
          return;
      }

      const formattedData = { ...formData };
      if (formattedData.DOB) {
        formattedData.DOB = moment(formattedData.DOB, 'YYYY-MM-DD').format('YYYY-MM-DD');
      }

      await axios.put(`${API_BASE_URL}/${endpoint}/${idField}`, formattedData);
      toast.success(`${selectedUserType} updated successfully`);
      setIsModalOpen(false);
      setSelectedRecord(null);
      setSelectedUserType(null);
      setFormData({});
      if (selectedUserType === "doctor") await fetchDoctors();
      if (selectedUserType === "manager") await fetchmanagers();
      if (selectedUserType === "agent") await fetchAgents();
      if (selectedUserType === "admin") await fetchAdmins();
    } catch (error) {
      toast.error(`Error updating ${selectedUserType}`);
      console.error("Update error:", error);
    }
  };

  const openModal = (type, record, userType) => {
    setModalType(type);
    setSelectedRecord(record);
    setSelectedUserType(userType);
    setIsModalOpen(true);

    if (type === "edit" && record) {
      const formData = { ...record };
      if (formData.DOB) {
        formData.DOB = moment(formData.DOB).format('YYYY-MM-DD');
      }
      setFormData(formData);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedRecord(null);
    setSelectedUserType(null);
    setFormData({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleToggleStatus = async (record, userType) => {
    const recordId = record._id || record.id;
    const newStatus = record.status === "inactive" ? "active" : "inactive";
    let endpoint = "";
    let method = "put";

    switch (userType) {
      case "doctor": endpoint = "doctors"; break;
      case "agent": endpoint = "agents"; break;
      case "admin": endpoint = "admin"; method = "patch"; break;
      case "manager": endpoint = "managers"; break;
      case "patient": endpoint = "patients"; method = "patch"; break;
      default: return;
    }

    if (!endpoint || !recordId) return;

    const toggleKey = getDeleteKey(record, userType);
    try {
      console.log(`[Frontend] Toggling status for ${userType} ID: ${recordId}, New Status: ${newStatus}`);
      setStatusLoading((prev) => ({ ...prev, [toggleKey]: true }));
      const response = await axios[method](`${API_BASE_URL}/${endpoint}/${recordId}`, { status: newStatus });
      console.log(`[Frontend] Toggle response from backend for ${userType}:`, response.data);
      toast.success(`${userType} marked as ${newStatus}`);
      if (userType === "doctor") await fetchDoctors();
      if (userType === "agent") await fetchAgents();
      if (userType === "admin") {
        console.log(`[Frontend] Calling fetchAdmins() to refresh admin list`);
        await fetchAdmins();
      }
      if (userType === "manager") await fetchmanagers();
      if (userType === "patient") await fetchPatients();
    } catch (error) {
      toast.error(`Error updating ${userType} status`);
    } finally {
      setStatusLoading((prev) => ({ ...prev, [toggleKey]: false }));
    }
  };

  const getActionButtons = (record, userType) => {
    const deleteKey = getDeleteKey(record, userType);
    const isDeleting = Boolean(deleteLoading[deleteKey]);

    return (
      <div className="flex space-x-2">
        <button
          onClick={() => openModal("view", record, userType)}
          disabled={isDeleting}
          title="View"
          className={`px-2 py-1 text-sm bg-blue-600 text-white rounded-md transition-colors flex items-center justify-center ${isDeleting ? "opacity-60 cursor-not-allowed" : "hover:bg-blue-700"}`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        </button>
        <button
          onClick={() => openModal("edit", record, userType)}
          disabled={isDeleting}
          title="Edit"
          className={`px-2 py-1 text-sm bg-green-600 text-white rounded-md transition-colors flex items-center justify-center ${isDeleting ? "opacity-60 cursor-not-allowed" : "hover:bg-green-700"}`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
      </div>
    );
  };

  const getPatientActionButtons = (record) => (
    <div className="flex space-x-2">
      <button
        onClick={() => openModal("view", record, "patient")}
        title="View"
        className="px-2 py-1 text-sm bg-blue-600 text-white rounded-md transition-colors flex items-center justify-center hover:bg-blue-700"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      </button>
      <button
        onClick={() => {
          setEditPatientData(record);
          setEditPatientModalVisible(true);
        }}
        title="Edit"
        className="px-2 py-1 text-sm bg-green-600 text-white rounded-md transition-colors flex items-center justify-center hover:bg-green-700"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      </button>
    </div>
  );

  const doctorColumns = [
    {
      key: "image", label: "Image", render: (record) => (
        <img
          src={getImageUrl(record.image)}
          onError={handleImageError}
          alt="avatar"
          className="w-10 h-10 rounded-full object-cover"
        />
      )
    },
    { key: "docID", label: "ID", render: (record) => record.docID },
    { key: "docName", label: "Name", render: (record) => record.docName },
    { key: "department", label: "Department", render: (record) => record.department },
    { key: "email", label: "Email", render: (record) => record.email },
    { key: "mobile", label: "Mobile", render: (record) => record.mobile },
    {
      key: "status", label: "Status", render: (record) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleToggleStatus(record, "doctor")}
            disabled={statusLoading[getDeleteKey(record, "doctor")]}
            title={record.status === 'inactive' ? 'Click to Enable' : 'Click to Disable'}
            className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors focus:outline-none shadow-inner ${record.status === 'inactive' ? 'bg-gray-300 hover:bg-gray-400' : 'bg-emerald-500 hover:bg-emerald-600'
              } ${statusLoading[getDeleteKey(record, "doctor")] ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`flex items-center justify-center h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform`}
              style={{ transform: record.status === 'inactive' ? 'translateX(2px)' : 'translateX(22px)' }}
            >
              {statusLoading[getDeleteKey(record, "doctor")] && (
                <span className="block h-2.5 w-2.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              )}
            </span>
          </button>
          <span className={`text-xs font-semibold ${record.status === 'inactive' ? 'text-gray-500' : 'text-emerald-600'}`}>
            {record.status === 'inactive' ? 'Inactive' : 'Active'}
          </span>
        </div>
      )
    },
    { key: "actions", label: "Actions", render: (record) => getActionButtons(record, "doctor") },
  ];

  const managerColumns = [
    {
      key: "image", label: "Image", render: (record) => (
        <img
          src={getImageUrl(record.image)}
          onError={handleImageError}
          alt="avatar"
          className="w-10 h-10 rounded-full object-cover"
        />
      )
    },
    { key: "managerID", label: "ID", render: (record) => record.managerID },
    { key: "managerName", label: "Name", render: (record) => record.managerName },
    { key: "email", label: "Email", render: (record) => record.email },
    { key: "mobile", label: "Mobile", render: (record) => record.mobile },
    { key: "education", label: "Education", render: (record) => record.education },
    {
      key: "status", label: "Status", render: (record) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleToggleStatus(record, "manager")}
            disabled={statusLoading[getDeleteKey(record, "manager")]}
            title={record.status === 'inactive' ? 'Click to Enable' : 'Click to Disable'}
            className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors focus:outline-none shadow-inner ${record.status === 'inactive' ? 'bg-gray-300 hover:bg-gray-400' : 'bg-emerald-500 hover:bg-emerald-600'
              } ${statusLoading[getDeleteKey(record, "manager")] ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`flex items-center justify-center h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform`}
              style={{ transform: record.status === 'inactive' ? 'translateX(2px)' : 'translateX(22px)' }}
            >
              {statusLoading[getDeleteKey(record, "manager")] && (
                <span className="block h-2.5 w-2.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              )}
            </span>
          </button>
          <span className={`text-xs font-semibold ${record.status === 'inactive' ? 'text-gray-500' : 'text-emerald-600'}`}>
            {record.status === 'inactive' ? 'Inactive' : 'Active'}
          </span>
        </div>
      )
    },
    { key: "actions", label: "Actions", render: (record) => getActionButtons(record, "manager") },
  ];

  const agentColumns = [
    {
      key: "image", label: "Image", render: (record) => (
        <img
          src={getImageUrl(record.image)}
          onError={handleImageError}
          alt="avatar"
          className="w-10 h-10 rounded-full object-cover"
        />
      )
    },
    { key: "agentID", label: "ID", render: (record) => record.agentID },
    { key: "name", label: "Name", render: (record) => record.name },
    { key: "email", label: "Email", render: (record) => record.email },
    { key: "mobile", label: "Mobile", render: (record) => record.mobile },
    {
      key: "status", label: "Status", render: (record) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleToggleStatus(record, "agent")}
            disabled={statusLoading[getDeleteKey(record, "agent")]}
            title={record.status === 'inactive' ? 'Click to Enable' : 'Click to Disable'}
            className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors focus:outline-none shadow-inner ${record.status === 'inactive' ? 'bg-gray-300 hover:bg-gray-400' : 'bg-emerald-500 hover:bg-emerald-600'
              } ${statusLoading[getDeleteKey(record, "agent")] ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`flex items-center justify-center h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform`}
              style={{ transform: record.status === 'inactive' ? 'translateX(2px)' : 'translateX(22px)' }}
            >
              {statusLoading[getDeleteKey(record, "agent")] && (
                <span className="block h-2.5 w-2.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              )}
            </span>
          </button>
          <span className={`text-xs font-semibold ${record.status === 'inactive' ? 'text-gray-500' : 'text-emerald-600'}`}>
            {record.status === 'inactive' ? 'Inactive' : 'Active'}
          </span>
        </div>
      )
    },
    // { key: "commissionRate", label: "Commission Rate", render: (record) => `${record.commissionRate || 0}%` },
    { key: "actions", label: "Actions", render: (record) => getActionButtons(record, "agent") },
  ];

  const adminColumns = [
    {
      key: "image", label: "Image", render: (record) => (
        <img
          src={getImageUrl(record.image)}
          onError={handleImageError}
          alt="avatar"
          className="w-10 h-10 rounded-full object-cover"
        />
      )
    },
    { key: "adminID", label: "ID", render: (record) => record.adminID },
    { key: "adminName", label: "Name", render: (record) => record.adminName },
    { key: "email", label: "Email", render: (record) => record.email },
    { key: "mobile", label: "Mobile", render: (record) => record.mobile },
    { key: "education", label: "Education", render: (record) => record.education },
    {
      key: "status", label: "Status", render: (record) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleToggleStatus(record, "admin")}
            disabled={statusLoading[getDeleteKey(record, "admin")]}
            title={record.status === 'inactive' ? 'Click to Enable' : 'Click to Disable'}
            className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors focus:outline-none shadow-inner ${record.status === 'inactive' ? 'bg-gray-300 hover:bg-gray-400' : 'bg-emerald-500 hover:bg-emerald-600'
              } ${statusLoading[getDeleteKey(record, "admin")] ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`flex items-center justify-center h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform`}
              style={{ transform: record.status === 'inactive' ? 'translateX(2px)' : 'translateX(22px)' }}
            >
              {statusLoading[getDeleteKey(record, "admin")] && (
                <span className="block h-2.5 w-2.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              )}
            </span>
          </button>
          <span className={`text-xs font-semibold ${record.status === 'inactive' ? 'text-gray-500' : 'text-emerald-600'}`}>
            {record.status === 'inactive' ? 'Inactive' : 'Active'}
          </span>
        </div>
      )
    },
    { key: "actions", label: "Actions", render: (record) => getActionButtons(record, "admin") },
  ];

  const patientColumns = [
    {
      key: "image", label: "Image", render: (record) => (
        <img
          src={getImageUrl(record.image)}
          onError={handleImageError}
          alt="avatar"
          className="w-10 h-10 rounded-full object-cover"
        />
      )
    },
    { key: "patientName", label: "Name", render: (record) => record.patientName },
    { key: "mobile", label: "Mobile", render: (record) => record.mobile },
    { key: "age", label: "Age", render: (record) => record.age },
    { key: "gender", label: "Gender", render: (record) => record.gender },
    {
      key: "status", label: "Status", render: (record) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleToggleStatus(record, "patient")}
            disabled={statusLoading[getDeleteKey(record, "patient")]}
            title={record.status === 'inactive' ? 'Click to Enable' : 'Click to Disable'}
            className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors focus:outline-none shadow-inner ${record.status === 'inactive' ? 'bg-gray-300 hover:bg-gray-400' : 'bg-emerald-500 hover:bg-emerald-600'
              } ${statusLoading[getDeleteKey(record, "patient")] ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`flex items-center justify-center h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform`}
              style={{ transform: record.status === 'inactive' ? 'translateX(2px)' : 'translateX(22px)' }}
            >
              {statusLoading[getDeleteKey(record, "patient")] && (
                <span className="block h-2.5 w-2.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              )}
            </span>
          </button>
          <span className={`text-xs font-semibold ${record.status === 'inactive' ? 'text-gray-500' : 'text-emerald-600'}`}>
            {record.status === 'inactive' ? 'Inactive' : 'Active'}
          </span>
        </div>
      )
    },
    { key: "actions", label: "Actions", render: (record) => getPatientActionButtons(record) },
  ];

  const renderEditForm = () => {
    if (!selectedRecord || !selectedUserType) return null;

    const commonFields = (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input
            type="email"
            name="email"
            value={formData.email || ""}
            onChange={handleInputChange}
            required
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Mobile</label>
          <input
            type="text"
            name="mobile"
            value={formData.mobile || ""}
            onChange={handleInputChange}
            required
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
          <input
            type="number"
            name="age"
            value={formData.age || ""}
            onChange={handleInputChange}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
          <select
            name="gender"
            value={formData.gender || ""}
            onChange={handleInputChange}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
          <textarea
            name="address"
            value={formData.address || ""}
            onChange={handleInputChange}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
          <input
            type="date"
            name="DOB"
            value={formData.DOB || ""}
            onChange={handleInputChange}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>
    );

    switch (selectedUserType) {
      case "doctor":
        return (
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Doctor Name</label>
                <input
                  type="text"
                  name="docName"
                  value={formData.docName || ""}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <input
                  type="text"
                  name="department"
                  value={formData.department || ""}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Education</label>
                <input
                  type="text"
                  name="education"
                  value={formData.education || ""}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Blood Group</label>
                <select
                  name="bloodGroup"
                  value={formData.bloodGroup || ""}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Blood Group</option>
                  {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map((bg) => (
                    <option key={bg} value={bg}>{bg}</option>
                  ))}
                </select>
              </div>
            </div>
            {commonFields}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Details</label>
              <textarea
                name="details"
                value={formData.details || ""}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Update
              </button>
            </div>
          </form>
        );
      case "manager":
        return (
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">manager Name</label>
                <input
                  type="text"
                  name="managerName"
                  value={formData.managerName || ""}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Education</label>
                <input
                  type="text"
                  name="education"
                  value={formData.education || ""}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Blood Group</label>
                <select
                  name="bloodGroup"
                  value={formData.bloodGroup || ""}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Blood Group</option>
                  {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map((bg) => (
                    <option key={bg} value={bg}>{bg}</option>
                  ))}
                </select>
              </div>
            </div>
            {commonFields}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Details</label>
              <textarea
                name="details"
                value={formData.details || ""}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Update
              </button>
            </div>
          </form>
        );
      case "agent":
        return (
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Agent Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name || ""}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  name="status"
                  value={formData.status || ""}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Commission Rate (%)</label>
                <input
                  type="number"
                  name="commissionRate"
                  value={formData.commissionRate || ""}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            {commonFields}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                name="notes"
                value={formData.notes || ""}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Update
              </button>
            </div>
          </form>
        );
      case "admin":
        return (
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin Name</label>
                <input
                  type="text"
                  name="adminName"
                  value={formData.adminName || ""}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Education</label>
                <input
                  type="text"
                  name="education"
                  value={formData.education || ""}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            {commonFields}
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Update
              </button>
            </div>
          </form>
        );
      default:
        return null;
    }
  };

  const renderViewDetails = () => {
    if (!selectedRecord || !selectedUserType) return null;

    const getDescription = () => {
      switch (selectedUserType) {
        case "doctor":
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><strong>Doctor ID:</strong> {selectedRecord.docID}</div>
              <div><strong>Name:</strong> {selectedRecord.docName}</div>
              <div><strong>Department:</strong> {selectedRecord.department}</div>
              <div><strong>Email:</strong> {selectedRecord.email}</div>
              <div><strong>Mobile:</strong> {selectedRecord.mobile}</div>
              <div><strong>Age:</strong> {selectedRecord.age}</div>
              <div><strong>Gender:</strong> {selectedRecord.gender}</div>
              <div><strong>Blood Group:</strong> {selectedRecord.bloodGroup}</div>
              <div><strong>Date of Birth:</strong> {selectedRecord.DOB}</div>
              <div><strong>Address:</strong> {selectedRecord.address}</div>
              <div><strong>Education:</strong> {selectedRecord.education}</div>
              <div className="col-span-2"><strong>Details:</strong> {selectedRecord.details}</div>
            </div>
          );
        case "manager":
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><strong>manager ID:</strong> {selectedRecord.managerID}</div>
              <div><strong>Name:</strong> {selectedRecord.managerName}</div>
              <div><strong>Email:</strong> {selectedRecord.email}</div>
              <div><strong>Mobile:</strong> {selectedRecord.mobile}</div>
              <div><strong>Age:</strong> {selectedRecord.age}</div>
              <div><strong>Gender:</strong> {selectedRecord.gender}</div>
              <div><strong>Blood Group:</strong> {selectedRecord.bloodGroup}</div>
              <div><strong>Date of Birth:</strong> {selectedRecord.DOB}</div>
              <div><strong>Address:</strong> {selectedRecord.address}</div>
              <div><strong>Education:</strong> {selectedRecord.education}</div>
              <div className="col-span-2"><strong>Details:</strong> {selectedRecord.details}</div>
            </div>
          );
        case "agent":
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><strong>Agent ID:</strong> {selectedRecord.agentID}</div>
              <div><strong>Name:</strong> {selectedRecord.name}</div>
              <div><strong>Email:</strong> {selectedRecord.email}</div>
              <div><strong>Mobile:</strong> {selectedRecord.mobile}</div>
              <div><strong>Age:</strong> {selectedRecord.age}</div>
              <div><strong>Gender:</strong> {selectedRecord.gender}</div>
              <div><strong>Address:</strong> {selectedRecord.address}</div>
              <div><strong>Commission Rate:</strong> {selectedRecord.commissionRate}%</div>
              <div><strong>Status:</strong> {selectedRecord.status}</div>
              <div><strong>Referrals:</strong> {selectedRecord.referrals}</div>
              <div><strong>Total Commission:</strong> ₹{selectedRecord.totalCommission}</div>
              <div><strong>Date Joined:</strong> {selectedRecord.dateJoined ? new Date(selectedRecord.dateJoined).toLocaleDateString() : 'N/A'}</div>
              <div className="col-span-2"><strong>Notes:</strong> {selectedRecord.notes}</div>
            </div>
          );
        case "admin":
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><strong>Admin ID:</strong> {selectedRecord.adminID}</div>
              <div><strong>Name:</strong> {selectedRecord.adminName}</div>
              <div><strong>Email:</strong> {selectedRecord.email}</div>
              <div><strong>Mobile:</strong> {selectedRecord.mobile}</div>
              <div><strong>Age:</strong> {selectedRecord.age}</div>
              <div><strong>Gender:</strong> {selectedRecord.gender}</div>
              <div><strong>Date of Birth:</strong> {selectedRecord.DOB}</div>
              <div><strong>Address:</strong> {selectedRecord.address}</div>
              <div><strong>Education:</strong> {selectedRecord.education}</div>
            </div>
          );
        case "patient":
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><strong>Name:</strong> {selectedRecord.patientName}</div>
              <div><strong>Email:</strong> {selectedRecord.email}</div>
              <div><strong>Mobile:</strong> {selectedRecord.mobile}</div>
              <div><strong>Age:</strong> {selectedRecord.age}</div>
              <div><strong>Gender:</strong> {selectedRecord.gender}</div>
              <div><strong>Address:</strong> {selectedRecord.address}</div>
            </div>
          );
        default:
          return null;
      }
    };

    return (
      <div className="text-center">
        <img
          src={selectedRecord.image || "/user.png"}
          alt="avatar"
          className="w-24 h-24 rounded-full mx-auto mb-4"
        />
        <h2 className="text-xl font-semibold text-gray-900">
          {selectedRecord.docName || selectedRecord.managerName || selectedRecord.name || selectedRecord.adminName || selectedRecord.patientName || "User"}
        </h2>
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">{getDescription()}</div>
      </div>
    );
  };

  const renderTable = (columns, data, paginationOptions) => {
    const shouldPaginate = Boolean(paginationOptions);
    const pageSize = shouldPaginate ? paginationOptions.pageSize : data.length || 0;
    const currentPage = shouldPaginate ? paginationOptions.currentPage : 0;
    const onPageChange = paginationOptions?.onPageChange;
    const startIndex = shouldPaginate ? currentPage * pageSize : 0;
    const endIndex = shouldPaginate ? startIndex + pageSize : data.length;
    const pageData = data.slice(startIndex, endIndex);
    const totalPages = shouldPaginate && pageSize > 0 ? Math.ceil(data.length / pageSize) : 1;
    const maxPageIndex = Math.max(totalPages - 1, 0);
    const displayStart = data.length === 0 ? 0 : startIndex + 1;
    const displayEnd = data.length === 0 ? 0 : Math.min(startIndex + pageSize, data.length);

    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100">
              {columns.map((col) => (
                <th key={col.key} className="p-3 text-left text-sm font-semibold text-gray-700">
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageData.map((record, index) => (
              <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                {columns.map((col) => {
                  const content = col.render(record);
                  const isEmpty = content === "" || content === null || content === undefined || (typeof content === "string" && content.trim() === "");
                  return (
                    <td key={col.key} className="p-3 border-b border-gray-200">
                      {isEmpty ? "--" : content}
                    </td>
                  );
                })}
              </tr>
            ))}
            {pageData.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="p-4 text-center text-gray-500">
                  No records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {shouldPaginate && data.length > 0 && (
          <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
            <span>
              Showing {displayStart}-{displayEnd} of {data.length}
            </span>
            <div className="space-x-2">
              <button
                type="button"
                onClick={() => onPageChange && onPageChange(Math.max(currentPage - 1, 0))}
                disabled={currentPage === 0}
                className={`px-3 py-1 rounded-md border ${currentPage === 0 ? "text-gray-400 border-gray-200 cursor-not-allowed" : "text-gray-700 border-gray-300 hover:bg-gray-100"}`}
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => onPageChange && onPageChange(Math.min(currentPage + 1, maxPageIndex))}
                disabled={currentPage >= maxPageIndex}
                className={`px-3 py-1 rounded-md border ${currentPage >= maxPageIndex ? "text-gray-400 border-gray-200 cursor-not-allowed" : "text-gray-700 border-gray-300 hover:bg-gray-100"}`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!isAdmin) {
    return (
      <div className="flex min-h-screen bg-gray-100">
        <Sidebar />
        <div className="flex-1 p-4 md:p-8 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600">Only administrators can access this page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar onCollapse={setSidebarCollapsed} />
        <div className={`flex-1 p-6 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0'}`}>
          <div className="flex-1 p-6 ml-40">
            <h1 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
              <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              Control Users - Admin Panel
            </h1>
            <div className="bg-white rounded-lg shadow-md p-6">
              {activeTab === null ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                  <button
                    className="p-4 rounded-lg shadow-sm border text-left transition-all flex flex-col justify-between bg-white border-gray-200 hover:border-blue-300 hover:shadow-md"
                    onClick={() => setActiveTab("doctors")}
                  >
                    <div className="flex items-center justify-between mb-2 w-full">
                      <span className="text-gray-500 font-medium">Doctors</span>
                      <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                    <div className="text-2xl font-bold text-gray-900">{summaryCounts.doctor ?? doctors.length}</div>
                  </button>
                  <button
                    className="p-4 rounded-lg shadow-sm border text-left transition-all flex flex-col justify-between bg-white border-gray-200 hover:border-blue-300 hover:shadow-md"
                    onClick={() => setActiveTab("managers")}
                  >
                    <div className="flex items-center justify-between mb-2 w-full">
                      <span className="text-gray-500 font-medium">Managers</span>
                      <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    </div>
                    <div className="text-2xl font-bold text-gray-900">{summaryCounts.manager ?? managers.length}</div>
                  </button>
                  <button
                    className="p-4 rounded-lg shadow-sm border text-left transition-all flex flex-col justify-between bg-white border-gray-200 hover:border-blue-300 hover:shadow-md"
                    onClick={() => setActiveTab("agents")}
                  >
                    <div className="flex items-center justify-between mb-2 w-full">
                      <span className="text-gray-500 font-medium">Agents</span>
                      <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div className="text-2xl font-bold text-gray-900">{summaryCounts.agent ?? agents.length}</div>
                  </button>
                  <button
                    className="p-4 rounded-lg shadow-sm border text-left transition-all flex flex-col justify-between bg-white border-gray-200 hover:border-blue-300 hover:shadow-md"
                    onClick={() => setActiveTab("admins")}
                  >
                    <div className="flex items-center justify-between mb-2 w-full">
                      <span className="text-gray-500 font-medium">Admins</span>
                      <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div className="text-2xl font-bold text-gray-900">{summaryCounts.admin ?? admins.length}</div>
                  </button>
                  <button
                    className="p-4 rounded-lg shadow-sm border text-left transition-all flex flex-col justify-between bg-white border-gray-200 hover:border-blue-300 hover:shadow-md"
                    onClick={() => setActiveTab("patients")}
                  >
                    <div className="flex items-center justify-between mb-2 w-full">
                      <span className="text-gray-500 font-medium">Patients</span>
                      <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 14v4m0 0v4m0-4h4m-4 0h-4M12 14a4 4 0 100-8 4 4 0 000 8zM4 22a8 8 0 1116 0" />
                      </svg>
                    </div>
                    <div className="text-2xl font-bold text-gray-900">{summaryCounts.patient ?? patients.length}</div>
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center">
                      <button
                        onClick={() => setActiveTab(null)}
                        className="mr-4 p-2 text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                      </button>
                      <h2 className="text-xl font-semibold text-gray-800 capitalize">{activeTab} List</h2>
                    </div>
                    {activeTab === "patients" && (
                      <button
                        onClick={() => setAddPatientModalVisible(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                        </svg>
                        Add New Patient
                      </button>
                    )}
                  </div>

                  {loading ? (
                    <div className="text-center p-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
                      <p className="mt-4 text-gray-600">Loading user data...</p>
                    </div>
                  ) : (
                    <>
                      {activeTab === "doctors" &&
                        renderTable(doctorColumns, doctors, {
                          pageSize: PAGE_SIZE,
                          currentPage: doctorPage,
                          onPageChange: setDoctorPage,
                        })}
                      {activeTab === "managers" &&
                        renderTable(managerColumns, managers, {
                          pageSize: PAGE_SIZE,
                          currentPage: managerPage,
                          onPageChange: setManagerPage,
                        })}
                      {activeTab === "agents" &&
                        renderTable(agentColumns, agents, {
                          pageSize: PAGE_SIZE,
                          currentPage: agentPage,
                          onPageChange: setAgentPage,
                        })}
                      {activeTab === "admins" &&
                        renderTable(adminColumns, admins, {
                          pageSize: PAGE_SIZE,
                          currentPage: adminPage,
                          onPageChange: setAdminPage,
                        })}
                      {activeTab === "patients" &&
                        renderTable(patientColumns, patients, {
                          pageSize: PAGE_SIZE,
                          currentPage: patientPage,
                          onPageChange: setPatientPage,
                        })}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        {isModalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
            <div className={`bg-white rounded-lg p-6 ${modalType === "view" ? "w-full max-w-3xl" : "w-full max-w-2xl"} max-h-[90vh] overflow-y-auto`}>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {modalType === "view" ? `View ${selectedUserType?.charAt(0).toUpperCase() + selectedUserType?.slice(1)} Details` : `Edit ${selectedUserType?.charAt(0).toUpperCase() + selectedUserType?.slice(1)}`}
              </h2>
              {modalType === "view" ? renderViewDetails() : renderEditForm()}
              {modalType === "view" && (
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={closeModal}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {deleteModalVisible && userToDelete && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
              <h2 className="text-xl font-bold text-red-600 mb-4 flex items-center">
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
                {userToDelete.userType === "doctor" || userToDelete.userType === "agent" ? "Confirm Disable" : "Confirm Deletion"}
              </h2>
              <p className="text-gray-700 mb-4">
                {userToDelete.userType === "doctor" || userToDelete.userType === "agent"
                  ? `Are you sure you want to disable this ${userToDelete.userType}? They will be marked as inactive and hidden from selection lists, but their historical reports and associations will remain intact.`
                  : `Are you sure you want to delete this ${userToDelete.userType}?`
                }
              </p>

              {isFetchingRelations ? (
                <div className="flex items-center text-sm text-gray-500 mb-4">
                  <span className="inline-block h-4 w-4 mr-2 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                  Checking for related records...
                </div>
              ) : deleteRelations ? (
                <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4 text-sm text-red-800">
                  <p className="font-semibold mb-1">Warning! This action will also affect:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    {deleteRelations.testOrders !== undefined && deleteRelations.testOrders > 0 && (
                      <li><strong>{deleteRelations.testOrders}</strong> Test Order(s) {userToDelete.userType === 'patient' ? 'will be permanently deleted.' : 'will lose their association to this user.'}</li>
                    )}
                    {deleteRelations.reports !== undefined && deleteRelations.reports > 0 && (
                      <li><strong>{deleteRelations.reports}</strong> Report(s) {userToDelete.userType === 'patient' ? 'will be permanently deleted.' : 'will lose their association to this user.'}</li>
                    )}
                    {((deleteRelations.testOrders === 0 || deleteRelations.testOrders === undefined) && (deleteRelations.reports === 0 || deleteRelations.reports === undefined)) && (
                      <li>No related records found. It is safe to delete.</li>
                    )}
                  </ul>
                  {userToDelete.userType === 'patient' && (deleteRelations.testOrders > 0 || deleteRelations.reports > 0) && (
                    <p className="mt-2 font-bold underline">This action cannot be undone.</p>
                  )}
                </div>
              ) : null}

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setDeleteModalVisible(false);
                    setUserToDelete(null);
                    setDeleteRelations(null);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-800 font-medium rounded-md hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={proceedWithDeletion}
                  disabled={isFetchingRelations}
                  className={`px-4 py-2 bg-red-600 text-white font-medium rounded-md transition-colors ${isFetchingRelations ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-700'}`}
                >
                  {userToDelete.userType === "doctor" || userToDelete.userType === "agent" ? `Yes, Disable ${userToDelete.userType}` : `Yes, Delete ${userToDelete.userType}`}
                </button>
              </div>
            </div>
          </div>
        )}

        <PatientEditModal
          open={editPatientModalVisible}
          patient={editPatientData}
          loading={editPatientLoading}
          onCancel={() => {
            setEditPatientModalVisible(false);
            setEditPatientData(null);
          }}
          onChange={handleEditPatientChange}
          onSubmit={handleEditPatientSubmit}
        />

        <PatientEditModal
          open={addPatientModalVisible}
          patient={addPatientData}
          loading={addPatientLoading}
          isAddMode={true}
          onCancel={() => {
            setAddPatientModalVisible(false);
            setAddPatientData({ patientName: "", mobile: "", age: "", gender: "", address: "" });
          }}
          onChange={handleAddPatientChange}
          onSubmit={handleAddPatientSubmit}
        />
      </div>
    </>
  );
};

export default AdminDashboard;
